import { createContext, useContext, useState } from 'react';
import {
  IUserContextType,
  IUser,
  IUserListData,
  TFetchLinkedUserList,
  TFetchUserDetail,
} from './types';
import { useError } from '@hook/useError';
import { CLIENT_API } from '@api/services';

const UserContext = createContext<IUserContextType | undefined>(undefined);

export function UserContextProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { handleError } = useError();
  const [userListData, setUserListData] = useState<IUserListData | null>(null);
  const [user, setUser] = useState<IUser | null>(null);

  const fetchLinkedUserList: TFetchLinkedUserList = async ({
    userId,
    requestPageNumber,
    limit,
    sortField,
    emailKeyword,
  }) => {
    try {
      const { data } = await CLIENT_API.UserApi.findLinkedUsers(userId, {
        requestPageNumber,
        limit,
        sortField,
        emailKeyword,
      });

      setUserListData(data);
    } catch (e) {
      handleError(e);
    }
  };

  const fetchUserDetail: TFetchUserDetail = async ({
    userId,
    linkedUserId,
  }) => {
    try {
      const { data } = await CLIENT_API.UserApi.findLinkedUser(
        userId,
        linkedUserId,
      );

      if (data) setUser(data);
    } catch (e) {
      handleError(e);
    }
  };

  return (
    <UserContext.Provider
      value={{ userListData, fetchLinkedUserList, fetchUserDetail, user }}
    >
      {children}
    </UserContext.Provider>
  );
}

export const useUserContext = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUserContext must be used within a UserContextProvider');
  }
  return context;
};
